import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  ArrowRight,
  Bell,
  FileText,
  Heart,
  LineChart,
  Shield,
  Sprout,
  Sun,
  TreePine,
  UserCheck,
} from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { track } from "@/lib/analytics";

const STEPS = [
  { num: "01", title: "Create your free account", desc: "A private, secure home for your wellness journey." },
  { num: "02", title: "Complete your wellness discovery", desc: "A personalized, reflective questionnaire you can move through at your own pace." },
  { num: "03", title: "Explore your visual profile", desc: "See your Rooted Wellness Profile across five dimensions of everyday life." },
  { num: "04", title: "Receive your extended report", desc: "A private, in-depth report saved to your account for whenever you need it." },
  { num: "05", title: "Return over time", desc: "Repeat your discovery and explore how your responses change as life changes." },
  { num: "06", title: "Book personal guidance", desc: "Schedule a consultation with Staci whenever you'd like a human conversation." },
];

const DIMENSION_CARDS = [
  {
    icon: Activity,
    name: "Physical Wellness",
    desc: "Daily rhythms, rest, nourishment, movement, and the quiet signals your body sends.",
  },
  {
    icon: TreePine,
    name: "Inheritance & Personal History",
    desc: "Family patterns and personal history that shape your starting point.",
    note: "Explored as context, never as a grade.",
  },
  {
    icon: Sprout,
    name: "Environmental Wellness",
    desc: "Your home, surroundings, and the spaces that quietly influence how you feel.",
  },
  {
    icon: Heart,
    name: "Emotional Wellness",
    desc: "Stress, connection, and the emotional load you carry through everyday life.",
  },
  {
    icon: Sun,
    name: "Spiritual Wellness",
    desc: "Meaning, purpose, and your connection to something larger than yourself.",
  },
];

const BENEFITS = [
  { icon: Shield, title: "Private and secure", desc: "Your answers and reports belong to you, in an isolated account only you can access." },
  { icon: LineChart, title: "Saved results and history", desc: "Every discovery is stored, so you can revisit past profiles whenever you like." },
  { icon: FileText, title: "Extended private reports", desc: "Go deeper with a full written report attached to each completed discovery." },
  { icon: Bell, title: "Gentle reassessment reminders", desc: "Optional nudges to check in with yourself again when the time feels right." },
  { icon: UserCheck, title: "Personal guidance when you want it", desc: "Bring your profile to a consultation with Staci for one-on-one support." },
];

export default function WellnessDiscovery() {
  useEffect(() => {
    document.title = "The Rooted Wellness Discovery | Rooted Healing";
    track("discovery_landing_visited");
  }, []);

  return (
    <main data-testid="wellness-discovery-page" className="pt-[76px]">
      <section className="relative overflow-hidden py-20 sm:py-28">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-sage/70 blur-3xl"
        />
        <div className="relative mx-auto max-w-3xl px-5 text-center sm:px-8">
          <Reveal>
            <Overline>A New Rooted Healing Experience</Overline>
            <h1
              data-testid="discovery-hero-heading"
              className="font-serif text-4xl leading-[1.12] tracking-tight text-forest sm:text-5xl lg:text-6xl"
            >
              The Rooted <em className="italic text-gold">Wellness Discovery</em>
            </h1>
            <p className="mx-auto mt-7 max-w-xl text-lg font-light leading-relaxed text-charcoal/75">
              Discover your starting point. Explore the patterns shaping your everyday
              experience. Begin a more intentional wellness journey.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-5">
              <Link
                to="/register"
                data-testid="discovery-hero-register-button"
                onClick={() => track("registration_cta_click", { location: "discovery-hero" })}
                className="group inline-flex items-center gap-2 rounded-full bg-forest px-8 py-4 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-forest-deep hover:shadow-[0_14px_35px_rgba(23,63,53,0.28)] hover:-translate-y-0.5"
              >
                Create My Free Wellness Account
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/login"
                data-testid="discovery-hero-login-link"
                className="text-sm font-semibold text-forest underline-offset-4 transition-colors duration-300 hover:text-gold-dark hover:underline"
              >
                Member Login
              </Link>
            </div>
            <p className="mt-5 text-sm text-taupe">
              Free to join. Your answers stay private to your account, always.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-sage/50 py-20 sm:py-28" data-testid="discovery-how-it-works">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <Overline>How It Works</Overline>
            <h2 className="font-serif text-3xl leading-tight text-forest sm:text-4xl">
              Six gentle steps, at your own pace
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
            {STEPS.map((s, i) => (
              <Reveal key={s.num} delay={(i % 3) * 0.1}>
                <div data-testid={`discovery-step-${i + 1}`} className="border-t border-forest/15 pt-7">
                  <span className="font-serif text-4xl text-gold/70">{s.num}</span>
                  <h3 className="mt-4 font-serif text-xl text-forest">{s.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-charcoal/70">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28" data-testid="discovery-dimensions">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <Overline>The Five Dimensions</Overline>
            <h2 className="font-serif text-3xl leading-tight text-forest sm:text-4xl lg:text-5xl">
              One whole life, explored in <em className="italic text-gold">five dimensions</em>
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {DIMENSION_CARDS.map((d, i) => (
              <Reveal key={d.name} delay={i * 0.07}>
                <article
                  data-testid={`dimension-card-${d.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`group flex h-full flex-col rounded-[1.75rem] border p-8 transition-all duration-500 hover:-translate-y-1.5 ${
                    d.note
                      ? "border-gold/35 bg-sage/60 hover:shadow-[0_24px_50px_rgba(180,146,83,0.15)]"
                      : "border-forest/12 bg-white/60 hover:shadow-[0_24px_50px_rgba(23,63,53,0.12)]"
                  }`}
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-forest/8">
                    <d.icon className="h-5 w-5 text-forest" />
                  </div>
                  <h3 className="mt-6 font-serif text-xl text-forest">{d.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/70">{d.desc}</p>
                  {d.note && (
                    <span className="mt-5 inline-flex w-fit rounded-full bg-gold/15 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-gold-dark">
                      {d.note}
                    </span>
                  )}
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-forest py-20 text-ivory sm:py-28" data-testid="discovery-benefits">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <Overline light>Membership Benefits</Overline>
            <h2 className="font-serif text-3xl leading-tight text-ivory sm:text-4xl lg:text-5xl">
              A private home for <em className="italic text-gold-soft">your journey</em>
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.07}>
                <div className="flex h-full flex-col rounded-[1.75rem] border border-ivory/15 bg-ivory/[0.04] p-8">
                  <b.icon className="h-6 w-6 text-gold-soft" />
                  <h3 className="mt-6 font-serif text-xl text-ivory">{b.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ivory/65">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 text-center sm:py-32" data-testid="discovery-final-cta">
        <div className="mx-auto max-w-2xl px-5 sm:px-8">
          <Reveal>
            <h2 className="font-serif text-3xl leading-snug text-forest sm:text-4xl lg:text-5xl">
              Ready to Discover Your <em className="italic text-gold">Wellness Starting Point?</em>
            </h2>
            <Link
              to="/register"
              data-testid="discovery-final-register-button"
              onClick={() => track("registration_cta_click", { location: "discovery-final" })}
              className="group mt-10 inline-flex items-center gap-2 rounded-full bg-forest px-9 py-4 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-forest-deep hover:shadow-[0_14px_35px_rgba(23,63,53,0.28)] hover:-translate-y-0.5"
            >
              Create My Free Wellness Account
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <p className="mx-auto mt-10 max-w-xl border-t border-forest/10 pt-8 text-sm leading-relaxed text-taupe">
              The Rooted Wellness Discovery is an educational experience. It reflects
              self-reported information and personal reflection, not medical diagnosis,
              treatment, or objective medical health measurements.
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
